import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'automationTemplate/trigger/customSurveySubmitted/unavailableSurveys';

/**
 * Surveys already claimed by an enabled automation, to warn before the enable-time check rejects.
 * Account-scoped so it includes another user's claim, which the customer cannot otherwise see.
 */
export function useCustomSurveySubmittedUnavailableSurveysQuery({
	enabled = true,
	impersonationContext,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} = {}) {
	const userSession = useUserSession();
	return useQuery<Api.ICustomSurveySubmittedUnavailableSurvey[]>({
		enabled,
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<Api.ICustomSurveySubmittedUnavailableSurvey[]>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'AutomationTemplate/Trigger/CustomSurveySubmitted/UnavailableSurveys',
				}),
				'GET'
			);
		},
		queryKey: [BASE_QUERY_KEY, impersonationContext?.account?.id],
		meta: {
			// A failed load must not read as "nothing is claimed" — that would mark every survey
			// selectable, which is the outcome this warning exists to prevent.
			toastErrorMessage: 'Error checking which surveys are already in use',
		},
	});
}

export const invalidateCustomSurveySubmittedUnavailableSurveys = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
